/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

interface SearchProps {
    /**
     * A string representing the CSS class to be applied to the SearchIcon element to override its default styling.
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     */
    color1?: string;
    /**
     * A number or string representing the height of the SearchIcon element.
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     */
    testId?: string;
    /**
     * A number or string representing the height of the SearchIcon element.
     */
    width?: number | string;
}

/**
 * The `Search` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `SearchProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the SearchIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the SearchIcon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the SearchIcon element.
 * @returns A React element representing the `Search` component with the specified properties.
 *
 * @example
 * ```tsx
 * const SearchComponent = <Search className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Search = ({
    className = '',
    color1 = '#0019A5',
    height = 24,
    ref,
    testId = 'Search',
    width = 24
}: SearchProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 24 24"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path clipRule="evenodd" d="M3.75 11a7.25 7.25 0 1 1 14.5 0 7.25 7.25 0 0 1-14.5 0Zm12.88 6.7a8.75 8.75 0 1 1 1.06-1.06l3.84 3.83a.75.75 0 1 1-1.06 1.06l-3.84-3.84Z" fill={color1} fillRule="evenodd" />
    </svg>
);

Search.displayName = 'Search';